import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";
import { getClerkToken } from "@/services/clerk";
import { getConfig } from "@/services/api/config";

export type MemberResource = {
  id: number;
  title: string;
  description: string;
  category: string;
  /** Speciality only — what they do, or what kind of thing it is. Place is
   *  `location` and provenance is `afell`, because the imported library mixed
   *  all three into one list and they behave nothing alike. */
  tags: string[];
  /** Where they are. Empty for anything that is read rather than hired. */
  location: string;
  email: string;
  phone: string;
  /** When it joined the library — *not* a deadline. Show it as "Added …". */
  date: string | null;
  /** Vouched for by AFELL, or published by Soft Network. */
  afell: boolean;
  access: "all" | "paid";
  locked: boolean;
  url: string | null;
  thread: { id: number; title: string } | null;
};

/** One filter's values with how many results each would leave. */
export type Facet = { value: string; count: number };

export type ResourceFacets = {
  categories: Facet[];
  tags: Facet[];
  locations: Facet[];
};

export type ResourceFilters = {
  search?: string;
  category?: string;
  tag?: string;
  location?: string;
};

export type AdminResource = {
  id: number;
  title: string;
  /** Where it files in A to Z when the automatic answer (a person by
   *  surname, anything else by title) is wrong. Usually blank. */
  sort_as?: string;
  description: string;
  url: string;
  category: string;
  tags: string[];
  location?: string;
  email?: string;
  phone?: string;
  /** The link as typed. `url` is what a member follows, which is our own copy
   *  when there is one. */
  link?: string;
  /** Our own copy of the document, when one has been uploaded. */
  file_url?: string | null;
  access: "all" | "paid";
  status: "pending" | "published" | "declined";
  submitter_name: string | null;
  thread_id: number | null;
  created_at: string;
};

export type ResourceInput = {
  title: string;
  sort_as?: string;
  description: string;
  /** Optional: a quarter of the library is a person and a way to reach them
   *  with no website at all. */
  url?: string;
  category: string;
  tags: string[];
  location?: string;
  email?: string;
  phone?: string;
  access?: "all" | "paid";
  thread_id?: number;
};

/**
 * The library, with counts for every filter that applies.
 *
 * Filtering is a round trip rather than a client-side narrow: the counts come
 * from the server, computed against the results narrowed by every filter
 * *except* the one being counted — which is what stops a filter offering a
 * value that would return nothing.
 */
export function useResources(filters: ResourceFilters) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value) query.set(key, value);
  }
  const qs = query.toString();
  return useQuery<{
    resources: MemberResource[];
    facets: ResourceFacets;
    /** The real size of the library, which in a preview is larger than the
     *  sample below — so the page can say what is behind the cut. */
    total: number;
    /** The viewer is not a member: a capped sample, with the link, the email
     *  and the phone number withheld, and no search. */
    preview?: boolean;
  }>({
    queryKey: ["afell", "resources", qs],
    queryFn: () => apiRequest(`/afell/resources/${qs ? `?${qs}` : ""}`),
    placeholderData: keepPreviousData,
  });
}

export function useSuggestResource() {
  const queryClient = useQueryClient();
  return useMutation<{ id: number }, Error, ResourceInput>({
    mutationFn: (payload) => apiRequest("/afell/resources/", "POST", payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["afell", "resources"] }),
  });
}

export function useAdminResources() {
  return useQuery<{ resources: AdminResource[] }>({
    queryKey: ["afell", "resources", "admin"],
    queryFn: () => apiRequest("/afell/admin/resources/"),
  });
}

function useAdminResourceInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["afell", "resources"] });
}

export function useCreateResource() {
  const invalidate = useAdminResourceInvalidate();
  return useMutation<{ id: number }, Error, ResourceInput>({
    mutationFn: (payload) => apiRequest("/afell/admin/resources/", "POST", payload),
    onSuccess: invalidate,
  });
}

/** Keep our own copy of a document rather than linking somebody else's, and
 *  drop it again. Multipart, so it goes round apiRequest's JSON encoding, the
 *  same as a legacy's picture. */
export function useResourceFile() {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; url: string | null }, Error, { id: number; file: File | null }>({
    mutationFn: async ({ id, file }) => {
      const headers: Record<string, string> = {};
      const token = await getClerkToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const path = `${getConfig().apiUrl}/afell/admin/resources/${id}/file/`;
      let response: Response;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        response = await fetch(path, { method: "POST", headers, body, credentials: "include" });
      } else {
        response = await fetch(path, { method: "DELETE", headers, credentials: "include" });
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Upload failed");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "admin", "resources"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "resources"] });
    },
  });
}

export function useUpdateResource() {
  const invalidate = useAdminResourceInvalidate();
  return useMutation<{ id: number }, Error, { id: number } & ResourceInput>({
    mutationFn: ({ id, ...payload }) =>
      apiRequest(`/afell/admin/resources/${id}/`, "POST", payload),
    onSuccess: invalidate,
  });
}

export function useReviewResource() {
  const invalidate = useAdminResourceInvalidate();
  return useMutation<unknown, Error, { id: number; decision: "published" | "declined" }>({
    mutationFn: ({ id, decision }) =>
      apiRequest(`/afell/admin/resources/${id}/`, "POST", { decision }),
    onSuccess: invalidate,
  });
}

export function useRemoveResource() {
  const invalidate = useAdminResourceInvalidate();
  return useMutation<unknown, Error, number>({
    mutationFn: (id) => apiRequest(`/afell/admin/resources/${id}/`, "DELETE"),
    onSuccess: invalidate,
  });
}


/** A resource this member added, and where it stands. */
export type MyResource = {
  id: number;
  title: string;
  description: string;
  url: string;
  category: string;
  tags: string[];
  location: string;
  email: string;
  phone: string;
  status: "pending" | "published" | "declined";
  date: string | null;
};

/** The vocabulary already in use, so a new entry lands on an existing tag
 *  rather than a near-miss: 38 speciality tags built up since 2019, and
 *  "photography" next to "Photographer" splits a shelf in two. */
export type ResourceVocabulary = {
  categories: string[];
  tags: string[];
  locations: string[];
};

export function useMyResources() {
  return useQuery<{ resources: MyResource[]; vocabulary: ResourceVocabulary }>({
    queryKey: ["afell", "resources", "mine"],
    queryFn: () => apiRequest("/afell/resources/mine/"),
  });
}
